import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { aggregateStats } from '@mahjong/core';
import {
  AppBar,
  BarAction,
  Body,
  Button,
  EmptyState,
  ErrorBox,
  Label,
  ListCard,
  RankBadge,
  Screen,
  Spinner,
  cx,
} from '../components/ui';
import { ShareSheet } from '../components/ShareSheet';
import { PeriodFilter, inPeriod } from '../components/PeriodFilter';
import type { PeriodRange } from '../components/PeriodFilter';
import { useGroup, useGroupToken, useMatches } from '../hooks/useGroup';
import { ApiError } from '../lib/api';
import { formatAverage, formatDateTime, formatPoint } from '../lib/format';


/**
 * グループホーム（§9.2 / §4.5）。
 * 累計ランキングが主役なので最上部に固定し、履歴は圧縮行で並べる。
 */
export function GroupHomePage() {
  const { groupId } = useParams<{ groupId: string }>();
  const token = useGroupToken(groupId);
  const [searchParams, setSearchParams] = useSearchParams();

  const [period, setPeriod] = useState<PeriodRange>({ period: 'all' });
  const [shareOpen, setShareOpen] = useState(searchParams.get('share') === '1');

  const group = useGroup(groupId, token);
  const matches = useMatches(groupId, token);

  function closeShare() {
    setShareOpen(false);
    if (searchParams.has('share')) {
      searchParams.delete('share');
      setSearchParams(searchParams, { replace: true });
    }
  }

  // --- トークンが無い（共有URL以外から来た） -------------------------------
  if (!token) {
    return (
      <Screen>
        <AppBar title="グループ" backTo="/" />
        <Body>
          <ErrorBox
            title="このグループを開けません"
            message="この端末に参加情報が保存されていません。グループの共有URL（#t=… を含むもの）から開いてください。"
          />
          <Link to="/">
            <Button variant="ghost">トップへ戻る</Button>
          </Link>
        </Body>
      </Screen>
    );
  }

  if (group.isLoading) {
    return (
      <Screen>
        <AppBar title="読み込み中" backTo="/" />
        <Body>
          <Spinner />
        </Body>
      </Screen>
    );
  }

  if (group.isError) {
    const err = group.error;
    const unauthorized = err instanceof ApiError && err.isUnauthorized;
    const notFound = err instanceof ApiError && err.status === 404;
    return (
      <Screen>
        <AppBar title="グループ" backTo="/" />
        <Body>
          <ErrorBox
            title={unauthorized ? '権限がありません' : notFound ? 'グループが見つかりません' : '読み込めませんでした'}
            message={
              unauthorized
                ? '共有URLが変わったか、グループが削除された可能性があります。'
                : err instanceof ApiError
                  ? err.message
                  : '通信に失敗しました。'
            }
          />
          <Button variant="ghost" onClick={() => group.refetch()}>
            再読み込み
          </Button>
          <Link to="/">
            <Button variant="ghost">トップへ戻る</Button>
          </Link>
        </Body>
      </Screen>
    );
  }

  const bundle = group.data!;
  const defaultRule = bundle.rules.find((r) => r.isDefault) ?? bundle.rules[0];
  const activeMembers = bundle.members.filter((m) => m.active);

  const matchList = matches.data?.matches ?? [];

  // 期間フィルタ（§4.5）。MVP では全件取得してクライアント側で絞る（§7.3）
  const filtered = matchList.filter((m) => inPeriod(m.playedAt, period));

  const stats = aggregateStats(filtered);

  // 履歴表の列。メンバーの並び順のまま、この期間に打った人だけを残す。
  // 非アクティブでも過去の対局には出てくるので active では絞らない。
  const columnMembers = [...bundle.members]
    .sort((a, b) => a.order - b.order)
    .filter((m) => filtered.some((match) => match.results.some((r) => r.memberId === m.memberId)));

  return (
    <Screen>
      <AppBar
        title={bundle.group.name}
        subtitle={[
          `${bundle.group.matchCount}戦`,
          defaultRule ? `${defaultRule.playerCount}人` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        backTo="/"
        action={
          <div className="flex items-center gap-3">
            <BarAction onClick={() => setShareOpen(true)}>共有</BarAction>
            <Link to={`/g/${groupId}/settings`} aria-label="グループ設定" className="text-felt-sub">
              設定
            </Link>
          </div>
        }
      />

      <Body className="gap-2.5 pb-24">
        <PeriodFilter value={period} onChange={setPeriod} />

        {/* --- 累計ランキング --- */}
        {stats.length === 0 ? (
          <EmptyState
            title={matchList.length === 0 ? 'まだ対局がありません' : 'この期間の対局はありません'}
            description={
              matchList.length === 0
                ? '「対局を記録」から最初の半荘を登録してください。'
                : '期間を変えると過去の成績が見られます。'
            }
          />
        ) : (
          <ListCard className="py-1">
            {stats.map((s, i) => (
              <Link
                key={s.memberId}
                to={`/g/${groupId}/stats/${s.memberId}`}
                className={cx('flex items-center gap-3 py-2.5', i > 0 && 'border-t border-line')}
              >
                <RankBadge rank={i + 1} />
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-bold">{s.nameSnapshot}</div>
                  <div className="mt-0.5 text-[11px] text-ink-faint">
                    {s.matchCount}戦 · 平均 {formatAverage(s.averageRank)}
                  </div>
                </div>
                <span
                  className={cx(
                    'ml-auto text-[23px] font-black tabular-nums',
                    s.totalPoint >= 0 ? 'pt-plus' : 'pt-minus',
                  )}
                >
                  {formatPoint(s.totalPoint)}
                </span>
                <span className="text-base text-ink-faint">›</span>
              </Link>
            ))}
          </ListCard>
        )}

        {/* メンバーの追加・編集は設定画面から行う */}
        {activeMembers.length === 0 && (
          <EmptyState
            title="メンバーがいません"
            description="対局を記録する前に、設定からメンバーを追加してください。"
          />
        )}

        {/* --- 最近の対局 --- */}
        {filtered.length > 0 && columnMembers.length > 0 && (
          <>
            <Label className="mt-1">最近の対局</Label>
            <ListCard className="overflow-x-auto py-1">
              {/*
                列はメンバーの並び順で固定する。対局ごとに順位順で並べると、
                同じ列に別の人の点が入って誰がいくら取ったのか読めない。
                期間内に打っていないメンバーは列に出さない。
              */}
              <div className="flex min-w-max items-center gap-2 border-b border-line pb-1.5">
                <span className="w-[62px] shrink-0" />
                {columnMembers.map((m) => (
                  <span
                    key={m.memberId}
                    className="w-[52px] shrink-0 truncate text-right text-[10px] text-ink-faint"
                    title={m.name}
                  >
                    {m.name}
                  </span>
                ))}
              </div>

              {filtered.map((match, i) => (
                <Link
                  key={match.matchId}
                  to={`/g/${groupId}/matches/${match.matchId}`}
                  className={cx(
                    'flex min-w-max items-center gap-2 py-2.5',
                    i > 0 && 'border-t border-line',
                  )}
                >
                  <span className="w-[62px] shrink-0 text-[11px] text-ink-faint">
                    {formatDateTime(match.playedAt)}
                  </span>
                  {columnMembers.map((m) => {
                    const r = match.results.find((x) => x.memberId === m.memberId);
                    return (
                      <span
                        key={m.memberId}
                        className={cx(
                          'w-[52px] shrink-0 text-right text-[13px] font-bold tabular-nums',
                          !r ? 'text-ink-faint' : r.totalPoint >= 0 ? 'pt-plus' : 'pt-minus',
                        )}
                      >
                        {r ? formatPoint(r.totalPoint) : '—'}
                      </span>
                    );
                  })}
                </Link>
              ))}
            </ListCard>
          </>
        )}

        {/* --- 適用中のルール --- */}
        {defaultRule && (
          <Link
            to={`/g/${groupId}/rules`}
            className="mt-1 flex items-center gap-2 rounded-lg bg-bone px-3 py-2.5 text-[11.5px] leading-relaxed text-ink-soft"
          >
            <span>
              {defaultRule.name}／{defaultRule.startPoints.toLocaleString('ja-JP')}持ち{' '}
              {defaultRule.returnPoints.toLocaleString('ja-JP')}返し／ウマ{' '}
              {defaultRule.uma.map((u) => formatPoint(u)).join(' ')}
            </span>
            <span className="ml-auto shrink-0 text-sm text-ink-faint">›</span>
          </Link>
        )}
      </Body>

      {/* --- FAB --- */}
      <div className="pointer-events-none sticky bottom-0 z-10 flex justify-end p-4">
        <Link
          to={`/g/${groupId}/matches/new`}
          className="pointer-events-auto rounded-full bg-felt px-5 py-3.5 text-sm font-bold text-bone-hi shadow-lg shadow-felt/40"
        >
          ＋ 対局を記録
        </Link>
      </div>

      {shareOpen && (
        <ShareSheet
          groupId={groupId!}
          token={token}
          groupName={bundle.group.name}
          onClose={closeShare}
        />
      )}
    </Screen>
  );
}
